import React from 'react';
import Link from 'next/link';
import { CheckCircle2, CalendarDays, Clock, Car, Mail, MessageSquare, Download, ArrowRight } from 'lucide-react';
import type { BookingState } from './BookingWizard';

interface Props {
  booking: BookingState;
}

export default function BookingConfirmation({ booking }: Props) {
  const { service, dateTime, clientDetails, paymentType, bookingId } = booking;
  const addonsTotal = service?.addons.reduce((sum, a) => sum + a.price, 0) ?? 0;
  const subtotal = (service?.price ?? 0) + addonsTotal;
  const depositAmount = Math.round(subtotal * 0.3);
  const paidNow = paymentType === 'deposit' ? depositAmount : subtotal;

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-6 py-12 animate-fade-in">
      <div className="w-full max-w-lg">
        {/* Success icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center mx-auto mb-5 animate-scale-in">
            <CheckCircle2 size={40} className="text-emerald-400" />
          </div>
          <h1 className="text-3xl font-extrabold text-foreground mb-2">Booking Confirmed!</h1>
          <p className="text-muted-foreground">
            You&apos;re all set. Confirmation details have been sent to your email and phone.
          </p>
        </div>

        {/* Booking card */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden mb-6">
          {/* Header */}
          <div className="px-6 py-4 gold-gradient flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-primary-foreground/70 uppercase tracking-wide">Booking Reference</p>
              <p className="text-xl font-extrabold text-primary-foreground font-mono">{bookingId}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-primary-foreground/70">Paid Now</p>
              <p className="text-xl font-extrabold text-primary-foreground tabular-nums">${paidNow}</p>
            </div>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Service</p>
                <p className="text-sm font-semibold text-foreground">{service?.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CalendarDays size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="text-sm font-semibold text-foreground">{dateTime?.date}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Time</p>
                <p className="text-sm font-semibold text-foreground">{dateTime?.time}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Car size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Vehicle</p>
                <p className="text-sm font-semibold text-foreground">
                  {clientDetails?.vehicleYear} {clientDetails?.vehicleMake} {clientDetails?.vehicleModel}
                </p>
              </div>
            </div>

            {paymentType === 'deposit' && (
              <div className="pt-4 border-t border-border">
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/8 border border-amber-500/20">
                  <Clock size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-300/80">
                    Remaining balance of <span className="font-bold">${subtotal - depositAmount}</span> is due on your appointment day.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Notification confirmation */}
          <div className="px-6 pb-6 space-y-2">
            <div className="flex items-center gap-2">
              <Mail size={13} className="text-emerald-400" />
              <span className="text-xs text-muted-foreground">
                Confirmation sent to <span className="text-foreground font-medium">{clientDetails?.email}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare size={13} className="text-emerald-400" />
              <span className="text-xs text-muted-foreground">
                SMS sent to <span className="text-foreground font-medium">{clientDetails?.phone}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 active:scale-95">
            <Download size={16} />
            Save Confirmation
          </button>
          <Link
            href="/"
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl gold-gradient text-primary-foreground text-sm font-bold transition-all duration-150 hover:opacity-90 active:scale-95"
          >
            Back to Home
            <ArrowRight size={16} />
          </Link>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Need to cancel or reschedule? Reply to your confirmation email or text us at (305) 555-0147
        </p>
      </div>
    </div>
  );
}